import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { HubTask, HubTaskStatus, TaskStep, TaskCollaborator, AnnouncementDoc, PracticeForm, PracticeField } from "../../mocks/hubTasks";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { useRole } from "../../contexts/RoleContext";
import { logActivity } from "../../lib/activityLog";
import CollabMailModal from "./CollabMailModal";
import HubMentorPanel from "./HubMentorPanel";

interface Props {
  tasks: HubTask[];
  hubTitle?: string;
  workspaceId?: string | null;
  hub?: string; // for completion sync: 'ar' | 'concert' | 'foundation' | 'ip' | 'campaign'
}

const statusMeta: Record<HubTaskStatus, { label: string; color: string; dot: string }> = {
  todo: { label: "대기", color: "text-[#555555]", dot: "bg-[#444444]" },
  inprogress: { label: "진행중", color: "text-amber-400/80", dot: "bg-amber-400" },
  done: { label: "완료", color: "text-emerald-400/70", dot: "bg-emerald-400/70" },
  overdue: { label: "기한초과", color: "text-red-400/70", dot: "bg-red-400" },
};

function DDayBadge({ dDay, status }: { dDay: number; status: HubTaskStatus }) {
  if (status === "done") return <span className="text-[10px] text-emerald-400/60 font-medium">완료</span>;
  if (dDay < 0) return <span className="text-[10px] text-red-400/80 font-bold bg-red-400/10 px-1.5 py-0.5 rounded">D+{Math.abs(dDay)}</span>;
  if (dDay === 0) return <span className="text-[10px] text-amber-400 font-bold bg-amber-400/10 px-1.5 py-0.5 rounded">D-Day</span>;
  if (dDay <= 3) return <span className="text-[10px] text-amber-400/90 font-semibold bg-amber-400/10 px-1.5 py-0.5 rounded">D-{dDay}</span>;
  return <span className="text-[10px] text-[#444444] bg-white/[0.04] px-1.5 py-0.5 rounded">D-{dDay}</span>;
}

function StepCard({ step }: { step: TaskStep }) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center shrink-0">
        <div className="w-7 h-7 rounded-full bg-[#4B6CF7]/15 border border-[#4B6CF7]/25 flex items-center justify-center shrink-0">
          <span className="text-[#7A9BFF] text-[11px] font-bold">{step.num}</span>
        </div>
        <div className="w-px flex-1 bg-white/[0.05] mt-2 mb-2 min-h-[12px]"></div>
      </div>
      <div className="flex-1 pb-4">
        <div className="text-[#CCCCCC] text-sm font-medium mb-1.5">{step.title}</div>
        <p className="text-[#666666] text-xs leading-relaxed">{step.detail}</p>
        {step.tip && (
          <div className="mt-2.5 flex gap-2 bg-[#4B6CF7]/5 border border-[#4B6CF7]/15 rounded-lg px-3 py-2">
            <i className="ri-lightbulb-flash-line text-[#7A9BFF]/70 text-xs mt-0.5 shrink-0"></i>
            <p className="text-[#7A9BFF]/70 text-[11px] leading-relaxed">{step.tip}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── 공고문 뷰어 모달 ─────────────────────────────────────
function AnnouncementModal({ doc, onClose }: { doc: AnnouncementDoc; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl max-h-[85vh] flex flex-col bg-white rounded-2xl overflow-hidden shadow-2xl">
        {/* 헤더 */}
        <div className="bg-[#1A1A1A] px-6 py-4 flex items-start justify-between shrink-0">
          <div>
            <div className="text-[10px] text-white/50 font-semibold uppercase tracking-widest mb-1">{doc.issuer} 공식 공고</div>
            <h2 className="text-white font-bold text-base leading-snug">{doc.title}</h2>
            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
              <span className="text-[11px] text-white/60">공고일: {doc.issueDate}</span>
              {doc.deadline && (
                <span className="text-[11px] text-amber-400/90 font-semibold flex items-center gap-1">
                  <i className="ri-alarm-warning-line text-xs"></i>마감: {doc.deadline}
                </span>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white/80 cursor-pointer transition-colors mt-0.5">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        {/* 안내 배너 */}
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-2.5 flex items-center gap-2 shrink-0">
          <i className="ri-lightbulb-flash-line text-amber-500 text-sm shrink-0"></i>
          <p className="text-amber-700 text-xs">이 공고문을 꼼꼼히 분석해 평가 기준과 필수 서류를 파악하세요. 심사 배점이 표시된 항목을 우선 확인하세요.</p>
        </div>
        {/* 본문 */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {doc.sections.map((section, i) => (
            <div key={i}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-4 rounded-full bg-[#1A1A1A] shrink-0"></div>
                <h3 className="text-[#1A1A1A] font-bold text-sm">{section.heading}</h3>
              </div>
              <div className="pl-3 border-l border-[#E0E0E0]">
                <p className="text-[#555555] text-sm leading-relaxed whitespace-pre-line">{section.content}</p>
              </div>
            </div>
          ))}
        </div>
        {/* 푸터 */}
        <div className="border-t border-[#EEEEEE] px-6 py-3 flex items-center justify-between shrink-0 bg-[#FAFAFA]">
          <span className="text-[11px] text-[#AAAAAA]">공식 공고문 | {doc.issuer}</span>
          <button type="button" onClick={onClose} className="text-sm text-[#1A1A1A] font-semibold bg-[#F0F0F0] hover:bg-[#E0E0E0] px-4 py-1.5 rounded-lg cursor-pointer transition-colors whitespace-nowrap">
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── 실습 양식 작성 모달 ─────────────────────────────────
function PracticeFormModal({ form, onClose }: { form: PracticeForm; onClose: () => void }) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const inputCls = "w-full bg-[#F9F9F9] border border-[#E0E0E0] focus:border-[#1A1A1A] outline-none text-[#1A1A1A] text-sm px-3 py-2 rounded-lg placeholder-[#BBBBBB] transition-colors";
  const textareaCls = "w-full bg-[#F9F9F9] border border-[#E0E0E0] focus:border-[#1A1A1A] outline-none text-[#1A1A1A] text-sm px-3 py-2.5 rounded-lg placeholder-[#BBBBBB] transition-colors resize-none leading-relaxed";

  const allRequiredFilled = form.fields.filter(f => f.required).every(f => values[f.id]?.trim());

  const handleSave = () => {
    if (!allRequiredFilled) return;
    setSubmitted(true);
  };

  const renderField = (field: PracticeField) => {
    const val = values[field.id] ?? "";
    const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setValues(prev => ({ ...prev, [field.id]: e.target.value }));

    return (
      <div key={field.id} className="mb-5">
        <label className="block text-xs font-semibold text-[#555555] mb-1.5">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {field.type === "textarea" ? (
          <textarea
            value={val}
            onChange={onChange}
            placeholder={field.placeholder}
            rows={4}
            maxLength={field.maxLength ?? 500}
            className={textareaCls}
          />
        ) : field.type === "select" ? (
          <select value={val} onChange={onChange} className={inputCls}>
            <option value="">선택하세요</option>
            {field.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        ) : (
          <input
            type={field.type}
            value={val}
            onChange={onChange}
            placeholder={field.placeholder}
            className={inputCls}
          />
        )}
        {field.hint && (
          <div className="mt-1.5 flex gap-1.5">
            <i className="ri-lightbulb-flash-line text-amber-500/80 text-xs mt-0.5 shrink-0"></i>
            <p className="text-xs text-[#888888] leading-relaxed">{field.hint}</p>
          </div>
        )}
        {field.type === "textarea" && field.maxLength && (
          <div className="text-right text-[10px] text-[#CCCCCC] mt-1">{val.length}/{field.maxLength}</div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl max-h-[90vh] flex flex-col bg-white rounded-2xl overflow-hidden shadow-2xl">
        {/* 헤더 */}
        <div className="bg-[#F5F5F5] border-b border-[#E0E0E0] px-6 py-4 flex items-start justify-between shrink-0">
          <div>
            <div className="text-[10px] text-[#AAAAAA] font-semibold uppercase tracking-widest mb-1">직접 작성 실습</div>
            <h2 className="text-[#1A1A1A] font-bold text-base">{form.title}</h2>
            {form.subtitle && <p className="text-[11px] text-[#888888] mt-0.5">{form.subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="text-[#CCCCCC] hover:text-[#888888] cursor-pointer transition-colors mt-0.5">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {submitted ? (
          <div className="flex-1 flex flex-col items-center justify-center py-12 px-6 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mb-4">
              <i className="ri-checkbox-circle-fill text-emerald-500 text-3xl"></i>
            </div>
            <h3 className="text-[#1A1A1A] font-bold text-lg mb-2">작성 완료!</h3>
            <p className="text-[#777777] text-sm leading-relaxed mb-6">
              실제 업무에서는 이 내용을 기반으로<br />AI 사전검토 후 결재를 제출하게 됩니다.
            </p>
            <div className="text-left bg-[#F9F9F9] rounded-xl border border-[#E0E0E0] p-4 w-full max-w-md">
              {form.fields.filter(f => values[f.id]).map(f => (
                <div key={f.id} className="mb-2 last:mb-0">
                  <div className="text-[10px] text-[#AAAAAA] font-semibold mb-0.5">{f.label}</div>
                  <div className="text-xs text-[#555555] whitespace-pre-wrap line-clamp-2">{values[f.id]}</div>
                </div>
              ))}
            </div>
            <button type="button" onClick={onClose} className="mt-6 text-sm text-white bg-[#1A1A1A] hover:bg-[#333333] px-6 py-2.5 rounded-xl cursor-pointer transition-colors whitespace-nowrap font-semibold">
              닫기
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {form.fields.map(renderField)}
            </div>
            <div className="border-t border-[#EEEEEE] px-6 py-3.5 flex items-center justify-between shrink-0 bg-[#FAFAFA]">
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-[#CCCCCC]">
                  필수 항목 {form.fields.filter(f => f.required && values[f.id]?.trim()).length}/{form.fields.filter(f => f.required).length} 완료
                </span>
                {allRequiredFilled && (
                  <span className="text-[11px] text-emerald-500 font-semibold flex items-center gap-1">
                    <i className="ri-check-line"></i>제출 가능
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={onClose} className="text-sm text-[#888888] border border-[#E0E0E0] hover:bg-[#F0F0F0] px-4 py-1.5 rounded-lg cursor-pointer transition-colors whitespace-nowrap">
                  취소
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!allRequiredFilled}
                  className="text-sm text-white bg-[#1A1A1A] hover:bg-[#333333] disabled:opacity-30 disabled:cursor-not-allowed px-5 py-1.5 rounded-lg cursor-pointer transition-colors whitespace-nowrap font-semibold"
                >
                  <i className="ri-save-line mr-1.5"></i>작성 완료
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function HubTaskPanel({ tasks, hubTitle, workspaceId, hub = "hub" }: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { jobRole } = useRole();
  const [taskStates, setTaskStates] = useState<Record<string, HubTaskStatus>>({});
  const [selectedId, setSelectedId] = useState<string | null>(
    tasks.find(t => t.status === "inprogress")?.id ?? tasks[0]?.id ?? null
  );
  const [overrides, setOverrides] = useState<Record<string, Partial<HubTask>>>({});
  const [collabMailTarget, setCollabMailTarget] = useState<{ task: HubTask; collaborator: TaskCollaborator } | null>(null);
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const [showPracticeForm, setShowPracticeForm] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const celebratedRef = useRef(false);

  // 태스크 변경 시 모달 닫기
  const handleSelectTask = (id: string) => {
    setSelectedId(id);
    setShowAnnouncement(false);
    setShowPracticeForm(false);
  };

  // Fetch guide overrides from Supabase
  useEffect(() => {
    if (!workspaceId) return;
    const taskIds = tasks.map(t => t.id);
    supabase
      .from("hub_guide_overrides")
      .select("task_id, title, detail, caution, reference_text, steps")
      .eq("workspace_id", workspaceId)
      .in("task_id", taskIds)
      .then(({ data }) => {
        if (!data) return;
        const map: Record<string, Partial<HubTask>> = {};
        data.forEach((row) => {
          map[row.task_id] = {
            ...(row.title ? { title: row.title } : {}),
            ...(row.detail ? { detail: row.detail } : {}),
            ...(row.caution ? { caution: row.caution } : {}),
            ...(row.reference_text ? { reference: row.reference_text } : {}),
            ...(Array.isArray(row.steps) && row.steps.length > 0 ? { steps: row.steps as TaskStep[] } : {}),
          };
        });
        setOverrides(map);
      });
  }, [workspaceId, tasks]);

  // Fetch existing task completions from Supabase
  const fetchCompletions = useCallback(async () => {
    if (!workspaceId || !user?.id) return;
    const taskIds = tasks.map(t => t.id);
    const { data } = await supabase
      .from("task_completions")
      .select("task_id")
      .eq("workspace_id", workspaceId)
      .eq("user_id", user.id)
      .in("task_id", taskIds);
    if (data) {
      const completedMap: Record<string, HubTaskStatus> = {};
      data.forEach(row => { completedMap[row.task_id] = "done"; });
      setTaskStates(completedMap);
    }
  }, [workspaceId, user?.id, tasks]);

  useEffect(() => { fetchCompletions(); }, [fetchCompletions]);

  // Merge base task with override
  const mergeTask = (task: HubTask): HubTask => {
    const ov = overrides[task.id];
    if (!ov) return task;
    return { ...task, ...ov };
  };

  const getStatus = (task: HubTask): HubTaskStatus => taskStates[task.id] ?? task.status;

  const toggleDone = async (id: string) => {
    const cur = taskStates[id] ?? tasks.find(t => t.id === id)?.status ?? "todo";
    const next: HubTaskStatus = cur === "done" ? "todo" : "done";
    setTaskStates(prev => ({ ...prev, [id]: next }));

    // logActivity on completion
    if (next === "done") {
      const completedTask = tasks.find(t => t.id === id);
      if (completedTask) {
        logActivity(`${hubTitle ?? "허브"} · ${completedTask.title} 완료`, "task_complete");
      }
    }

    // Sync to Supabase
    if (workspaceId && user?.id) {
      if (next === "done") {
        await supabase.from("task_completions").upsert({
          workspace_id: workspaceId,
          user_id: user.id,
          task_id: id,
          hub,
          completed_at: new Date().toISOString(),
        }, { onConflict: "workspace_id,user_id,task_id" });
      } else {
        await supabase.from("task_completions").delete()
          .eq("workspace_id", workspaceId)
          .eq("user_id", user.id)
          .eq("task_id", id);
      }
    }
  };

  const resolved = tasks.map(t => ({ ...mergeTask(t), status: getStatus(t) }));
  const selected = resolved.find(t => t.id === selectedId) ?? null;
  const doneCount = resolved.filter(t => t.status === "done").length;

  useEffect(() => {
    if (tasks.length > 0 && doneCount === tasks.length && !celebratedRef.current) {
      celebratedRef.current = true;
      setTimeout(() => setShowCelebration(true), 400);
    }
  }, [doneCount, tasks.length]);
  const urgentCount = resolved.filter(t => t.status !== "done" && t.dDay <= 5).length;
  const collabCount = resolved.filter(t => t.isCollaboration).length;

  return (
    <div className="flex gap-0" style={{ minHeight: "calc(100vh - 180px)" }}>

      {/* ── Left: Task List ────────────────────────────── */}
      <div className="w-72 shrink-0 border-r border-white/[0.05] flex flex-col">
        {/* Summary */}
        <div className="px-4 py-3 border-b border-white/[0.05] bg-[#111112]">
          <div className="text-[#888888] text-xs font-medium mb-1.5">
            {hubTitle ?? ""} 할 일
          </div>
          <div className="flex items-center gap-3 text-[11px] flex-wrap">
            <span className="text-[#CCCCCC]">전체 <strong className="text-[#F2F2F2]">{tasks.length}</strong></span>
            <span className="text-[#555555]">·</span>
            <span>완료 <strong className="text-emerald-400/80">{doneCount}</strong></span>
            {collabCount > 0 && (
              <>
                <span className="text-[#555555]">·</span>
                <span className="text-emerald-400/60">협업 {collabCount}건</span>
              </>
            )}
            {urgentCount > 0 && (
              <>
                <span className="text-[#555555]">·</span>
                <span className="text-amber-400/80 font-semibold">마감임박 {urgentCount}건</span>
              </>
            )}
          </div>
          {tasks.length > 0 && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 bg-white/[0.04] rounded-full h-1">
                <div
                  className="h-1 rounded-full bg-[#4B6CF7]/60 transition-all duration-500"
                  style={{ width: `${Math.round((doneCount / tasks.length) * 100)}%` }}
                />
              </div>
              <span className="text-[#555555] text-[10px]">{Math.round((doneCount / tasks.length) * 100)}%</span>
            </div>
          )}
        </div>

        {/* Task Items */}
        <div className="flex-1 overflow-y-auto">
          {resolved.map(task => {
            const meta = statusMeta[task.status];
            const isSelected = selectedId === task.id;
            const isDone = task.status === "done";
            const hasOverride = !!overrides[task.id];
            return (
              <button
                key={task.id}
                type="button"
                onClick={() => handleSelectTask(task.id)}
                className={`w-full text-left px-4 py-3.5 border-b border-white/[0.04] transition-all cursor-pointer ${
                  isSelected
                    ? "bg-[#4B6CF7]/10 border-l-2 border-l-[#4B6CF7]"
                    : "hover:bg-white/[0.02] border-l-2 border-l-transparent"
                }`}
              >
                <div className="flex items-start gap-2 mb-1.5">
                  <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${
                    task.isCollaboration ? "bg-emerald-400/70" : meta.dot
                  }`}></div>
                  <span className={`text-xs font-medium flex-1 leading-snug ${isDone ? "text-[#555555] line-through" : isSelected ? "text-[#C8D8FF]" : "text-[#BBBBBB]"}`}>
                    {task.title}
                  </span>
                  {hasOverride && (
                    <span className="shrink-0 text-[9px] text-[#4B6CF7]/50 mt-0.5">
                      <i className="ri-edit-line"></i>
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 pl-3.5 flex-wrap">
                  {task.isCollaboration ? (
                    <span className="text-[10px] text-emerald-400/60">협업</span>
                  ) : (
                    <span className={`text-[10px] ${meta.color}`}>{meta.label}</span>
                  )}
                  <span className="text-[#444444] text-[10px]">·</span>
                  <DDayBadge dDay={task.dDay} status={task.status} />
                  {task.approvalTemplateId && !isDone && (
                    <span className="text-[9px] text-[#4B6CF7]/60 border border-[#4B6CF7]/20 px-1 py-0.5 rounded ml-auto">결재</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Right: Detail Guide Panel ──────────────────── */}
      <div className="flex-1 overflow-y-auto">
        {selected ? (
          <div className="p-6 max-w-2xl">
            {/* Task Header */}
            <div className="mb-5">
              <div className="flex items-start gap-3 mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {selected.isCollaboration && (
                      <span className="text-[10px] text-emerald-400/80 bg-emerald-400/10 border border-emerald-400/20 px-2 py-0.5 rounded-full font-semibold">
                        <i className="ri-team-line mr-1"></i>직무간 협업
                      </span>
                    )}
                    <h2 className={`text-base font-semibold ${selected.status === "done" ? "text-[#555555] line-through" : "text-[#F2F2F2]"}`}>
                      {selected.title}
                    </h2>
                    <DDayBadge dDay={selected.dDay} status={selected.status} />
                    {selected.approvalTemplateId && selected.status !== "done" && (
                      <span className="text-[9px] text-[#7A9BFF] border border-[#4B6CF7]/25 bg-[#4B6CF7]/5 px-1.5 py-0.5 rounded">결재 필요</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-[#555555]">
                    <span><i className="ri-calendar-line mr-1"></i>{selected.dueDate}</span>
                    <span><i className="ri-user-line mr-1"></i>{selected.assignee}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => toggleDone(selected.id)}
                  className={`shrink-0 flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
                    selected.status === "done"
                      ? "text-emerald-400/70 border-emerald-400/20 bg-emerald-400/5 hover:bg-emerald-400/10"
                      : "text-[#555555] border-white/[0.08] hover:text-[#888888] hover:border-white/[0.15]"
                  }`}
                >
                  <i className={selected.status === "done" ? "ri-checkbox-circle-line text-sm" : "ri-checkbox-blank-circle-line text-sm"}></i>
                  {selected.status === "done" ? "완료됨" : "완료 처리"}
                </button>
              </div>

              <p className="text-[#777777] text-sm leading-relaxed bg-white/[0.02] border border-white/[0.05] rounded-xl px-4 py-3">
                {selected.detail}
              </p>
            </div>

            {/* ── 협업 파트너 ── */}
            {selected.isCollaboration && selected.collaborators && selected.collaborators.length > 0 && (
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 flex items-center justify-center rounded bg-emerald-400/15">
                    <i className="ri-team-line text-emerald-400/70 text-xs"></i>
                  </div>
                  <span className="text-[#888888] text-xs font-medium uppercase tracking-widest">협업 파트너</span>
                </div>
                <div className="space-y-2.5">
                  {selected.collaborators.map((collab, i) => (
                    <div key={i} className="bg-[#111112] border border-emerald-400/10 rounded-xl px-4 py-3">
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-5 h-5 flex items-center justify-center shrink-0">
                          <i className="ri-group-line text-emerald-400/60 text-xs"></i>
                        </div>
                        <span className="text-[11px] text-emerald-400/80 font-semibold">{collab.team}</span>
                      </div>
                      <p className="text-[#777777] text-xs mb-2 pl-7">{collab.role}</p>
                      {collab.contactTip && (
                        <div className="ml-7 flex gap-1.5 bg-emerald-400/5 border border-emerald-400/10 rounded-lg px-2.5 py-2 mb-3">
                          <i className="ri-mail-send-line text-emerald-400/50 text-[10px] mt-0.5 shrink-0"></i>
                          <p className="text-emerald-400/50 text-[10px] leading-relaxed">{collab.contactTip}</p>
                        </div>
                      )}
                      {workspaceId && (
                        <div className="pl-7">
                          <button
                            type="button"
                            onClick={() => setCollabMailTarget({ task: selected, collaborator: collab })}
                            className="flex items-center gap-1.5 text-xs text-emerald-400/80 bg-emerald-400/10 hover:bg-emerald-400/20 border border-emerald-400/20 px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap font-medium"
                          >
                            <i className="ri-send-plane-line text-sm"></i>
                            협업 요청 메일 보내기
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── 업무 가이드 Steps ── */}
            {selected.steps && selected.steps.length > 0 && (
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-5 h-5 flex items-center justify-center rounded bg-[#4B6CF7]/15">
                    <i className="ri-route-line text-[#7A9BFF] text-xs"></i>
                  </div>
                  <span className="text-[#888888] text-xs font-medium uppercase tracking-widest">업무 진행 가이드</span>
                </div>
                <div className="bg-[#111112] border border-white/[0.06] rounded-xl px-5 py-4">
                  {selected.steps.map(step => (
                    <StepCard key={step.num} step={step} />
                  ))}
                </div>
              </div>
            )}

            {/* ── 주의사항 ── */}
            {selected.caution && (
              <div className="mb-4 flex gap-3 bg-amber-400/[0.04] border border-amber-400/20 rounded-xl px-4 py-3.5">
                <i className="ri-error-warning-line text-amber-400/70 text-base mt-0.5 shrink-0"></i>
                <div>
                  <div className="text-amber-400/70 text-[10px] font-semibold uppercase tracking-widest mb-1">주의사항</div>
                  <p className="text-[#AAAAAA] text-xs leading-relaxed">{selected.caution}</p>
                </div>
              </div>
            )}

            {/* ── 참고자료 ── */}
            {selected.reference && (
              <div className="mb-5 flex gap-3 bg-white/[0.02] border border-white/[0.06] rounded-xl px-4 py-3">
                <i className="ri-book-open-line text-[#555555] text-sm mt-0.5 shrink-0"></i>
                <div>
                  <div className="text-[#555555] text-[10px] font-semibold uppercase tracking-widest mb-1">참고자료</div>
                  <p className="text-[#666666] text-xs leading-relaxed">{selected.reference}</p>
                </div>
              </div>
            )}

            {/* ── 공고문 / 분석 문서 버튼 ── */}
            {selected.announcement && (
              <div className="mb-5 flex gap-3 bg-[#1A1A1A]/[0.04] border border-[#1A1A1A]/[0.08] rounded-xl px-4 py-3.5">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#1A1A1A] shrink-0">
                  <i className="ri-file-paper-2-line text-white text-base"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className="text-[10px] text-[#888888] font-semibold uppercase tracking-widest">분석 필수 공고문</span>
                    {selected.announcement.deadline && (
                      <span className="text-[10px] text-amber-500 font-semibold flex items-center gap-0.5">
                        <i className="ri-alarm-warning-line text-[9px]"></i>마감 {selected.announcement.deadline}
                      </span>
                    )}
                  </div>
                  <p className="text-[#1A1A1A] text-xs font-medium truncate">{selected.announcement.title}</p>
                  <p className="text-[#888888] text-[10px] mt-0.5">{selected.announcement.issuer} · {selected.announcement.sections.length}개 항목</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAnnouncement(true)}
                  className="shrink-0 flex items-center gap-1.5 text-xs text-[#1A1A1A] bg-[#1A1A1A]/[0.06] hover:bg-[#1A1A1A]/[0.12] border border-[#1A1A1A]/[0.12] px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap font-semibold self-center"
                >
                  <i className="ri-eye-line text-sm"></i>공고문 보기
                </button>
              </div>
            )}

            {/* ── 실습 양식 작성 버튼 ── */}
            {selected.practiceForm && (
              <div className="mb-5 flex gap-3 bg-[#4B6CF7]/[0.05] border border-[#4B6CF7]/[0.15] rounded-xl px-4 py-3.5">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#4B6CF7] shrink-0">
                  <i className="ri-edit-2-line text-white text-base"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] text-[#7A9BFF] font-semibold uppercase tracking-widest mb-0.5">직접 작성 실습</div>
                  <p className="text-[#CCCCCC] text-xs font-medium truncate">{selected.practiceForm.title}</p>
                  <p className="text-[#666666] text-[10px] mt-0.5">
                    {selected.practiceForm.subtitle ?? `${selected.practiceForm.fields.length}개 항목 · 실제 양식으로 연습`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPracticeForm(true)}
                  className="shrink-0 flex items-center gap-1.5 text-xs text-white bg-[#4B6CF7] hover:bg-[#5B7CF7] px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap font-semibold self-center"
                >
                  <i className="ri-edit-line text-sm"></i>작성하기
                </button>
              </div>
            )}

            {/* ── 산출물 & 결재 버튼 ── */}
            {selected.deliverable && (
              <div className="flex items-center justify-between gap-3 pt-4 border-t border-white/[0.05]">
                <div className="flex items-center gap-2 min-w-0">
                  <i className="ri-file-text-line text-[#4B6CF7]/50 shrink-0"></i>
                  <div>
                    <div className="text-[#555555] text-[10px] mb-0.5">제출 산출물</div>
                    <div className="text-[#666666] text-xs">{selected.deliverable}</div>
                  </div>
                </div>
                {selected.approvalTemplateId && selected.status !== "done" && (
                  <button
                    type="button"
                    onClick={() => navigate("/approval", { state: { templateId: selected.approvalTemplateId } })}
                    className="shrink-0 flex items-center gap-1.5 text-sm text-white bg-[#4B6CF7] hover:bg-[#5B7CF7] px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap font-medium"
                  >
                    <i className="ri-send-plane-line"></i>
                    결재 제출하기
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-center">
            <div>
              <i className="ri-cursor-line text-3xl text-[#444444] block mb-3"></i>
              <p className="text-[#555555] text-sm">왼쪽에서 할 일을 선택하세요</p>
            </div>
          </div>
        )}
      </div>

      {/* CollabMailModal */}
      {collabMailTarget && workspaceId && (
        <CollabMailModal
          task={collabMailTarget.task}
          collaborator={collabMailTarget.collaborator}
          workspaceId={workspaceId}
          senderRole={jobRole}
          onClose={() => setCollabMailTarget(null)}
        />
      )}

      {/* 공고문 뷰어 모달 */}
      {showAnnouncement && selected?.announcement && (
        <AnnouncementModal doc={selected.announcement} onClose={() => setShowAnnouncement(false)} />
      )}

      {/* 실습 양식 모달 */}
      {showPracticeForm && selected?.practiceForm && (
        <PracticeFormModal form={selected.practiceForm} onClose={() => setShowPracticeForm(false)} />
      )}

      {/* AI 멘토 사이드 패널 */}
      <HubMentorPanel tasks={tasks} taskStates={taskStates} hubTitle={hubTitle} />

      {/* ── 전체 완료 축하 팝업 ── */}
      {showCelebration && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="w-full max-w-md bg-[#111112] border border-white/[0.08] rounded-2xl overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-br from-[#4B6CF7]/20 to-emerald-400/10 px-6 py-7 text-center">
              <div className="text-4xl mb-3">🎉</div>
              <h2 className="text-[#F2F2F2] font-bold text-xl mb-1">{hubTitle ?? "허브"} 전체 완료!</h2>
              <p className="text-[#888888] text-sm">모든 태스크를 완료했어요. 정말 수고하셨습니다!</p>
            </div>

            {/* 다음 단계 가이드 */}
            <div className="px-6 pt-4 pb-3 space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-route-line text-[#7A9BFF] text-sm"></i>
                <span className="text-[#CCCCCC] text-xs font-semibold">다음 단계 가이드</span>
              </div>
              {[
                { icon: "ri-file-text-line", text: "결재 페이지에서 산출물 최종 제출", path: "/approval" },
                { icon: "ri-dashboard-line", text: "대시보드에서 명성·자본금 성과 확인", path: "/dashboard" },
              ].map((item) => (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => { setShowCelebration(false); navigate(item.path); }}
                  className="w-full flex items-center gap-3 bg-white/[0.03] border border-white/[0.05] hover:bg-white/[0.06] rounded-xl px-4 py-2.5 text-left cursor-pointer transition-colors"
                >
                  <i className={`${item.icon} text-emerald-400/70 text-sm shrink-0`}></i>
                  <span className="text-[#AAAAAA] text-xs">{item.text}</span>
                  <i className="ri-arrow-right-line text-[#444444] text-xs ml-auto shrink-0"></i>
                </button>
              ))}
            </div>

            {/* 다른 허브 바로가기 */}
            <div className="px-6 pb-4">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-compass-3-line text-[#888888] text-sm"></i>
                <span className="text-[#666666] text-xs font-semibold">다른 허브로 이동</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {([
                  { label: "A&R 허브", icon: "ri-music-2-line", path: "/ar-hub", key: "ar" },
                  { label: "마케팅", icon: "ri-megaphone-line", path: "/marketing", key: "marketing" },
                  { label: "캠페인 허브", icon: "ri-flag-line", path: "/campaign-hub", key: "campaign" },
                  { label: "콘서트 허브", icon: "ri-mic-line", path: "/concert-hub", key: "concert" },
                  { label: "IP 허브", icon: "ri-copyright-line", path: "/ip-hub", key: "ip" },
                  { label: "파운데이션", icon: "ri-building-line", path: "/foundation-hub", key: "foundation" },
                ] as const).filter(h => h.key !== hub).slice(0, 3).map(h => (
                  <button
                    key={h.path}
                    type="button"
                    onClick={() => { setShowCelebration(false); navigate(h.path); }}
                    className="flex flex-col items-center gap-1.5 py-3 bg-white/[0.03] border border-white/[0.05] hover:bg-[#4B6CF7]/10 hover:border-[#4B6CF7]/20 rounded-xl cursor-pointer transition-all"
                  >
                    <i className={`${h.icon} text-[#555555] text-base`}></i>
                    <span className="text-[#666666] text-[10px] font-medium whitespace-nowrap">{h.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="px-6 pb-5">
              <button type="button" onClick={() => setShowCelebration(false)}
                className="w-full py-2.5 bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] text-[#888888] text-sm rounded-xl cursor-pointer transition-colors whitespace-nowrap">
                계속 여기 있기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
